import type { ChecklistItem, ChecklistProgress } from '../../api/types';
import { checklistSourceLabel } from '../../lib/stages';

/**
 * Узел 50 мини кладёт в пункт `source`: `assignment` | `inquiry` | `applicant`,
 * куратор дописывает пункты с `agent`. Значения `applicant` в общем справочнике нет —
 * добиваем его здесь, остальное берём из `lib/stages`.
 */
const SOURCE_OVERRIDES: Record<string, string> = {
    applicant: 'уточнить у заявителя',
};

function sourceTag(item: ChecklistItem, index: number): { text: string; cls: string } {
    const key = item.source ?? '';
    const label = SOURCE_OVERRIDES[key] ?? checklistSourceLabel(key);
    if (!label) return { text: 'шаг ' + (index + 1), cls: 'check-src' };
    return { text: label, cls: 'check-src src-' + key };
}

export interface ChecklistProps {
    items: ChecklistItem[];
    progress: ChecklistProgress;
    /** `permissions.checklist`: отметки ставит исполнитель, главврач только смотрит. */
    canCheck: boolean;
    busy: boolean;
    onToggle: (checkId: string, done: boolean) => void;
}

/**
 * Карточка «Что нужно сделать по делу» (ui-case-card.md §8).
 * Пункт опознаётся по `item.id` (`chk_1`, …), а не по индексу: куратор дописывает пункты.
 */
export function Checklist({ items, progress, canCheck, busy, onToggle }: ChecklistProps) {
    if (items.length === 0) return null;

    const total = progress.total || items.length;
    const done = progress.done;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;

    return (
        <div className="checklist-card">
            <div className="checklist-head">
                <div className="checklist-title">Что нужно сделать по делу</div>
                <div className="checklist-progress">{done + ' из ' + total}</div>
            </div>
            <div className="checklist-bar">
                <span style={{ width: pct + '%' }} />
            </div>
            {items.map((item, index) => {
                const tag = sourceTag(item, index);
                return (
                    <div key={item.id} className={item.done ? 'check-item done' : 'check-item'}>
                        <button
                            type="button"
                            className={item.done ? 'check-box on' : 'check-box'}
                            disabled={!canCheck || busy}
                            title={canCheck ? undefined : 'Отметки ставит исполнитель'}
                            aria-label={item.done ? 'Снять отметку' : 'Отметить выполненным'}
                            aria-pressed={item.done}
                            onClick={() => onToggle(item.id, !item.done)}
                        >
                            <svg
                                viewBox="0 0 24 24"
                                width="12"
                                height="12"
                                fill="none"
                                stroke="#fff"
                                strokeWidth="3.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </button>
                        <span className={tag.cls}>{tag.text}</span>
                        <span className="check-text">{item.text}</span>
                    </div>
                );
            })}
        </div>
    );
}

export default Checklist;
